'use client';

import React, { useState } from 'react';
import { WeatherForecast as WeatherForecastType, WeatherDay } from '../types';
import { trackWeatherExpand } from '../lib/analytics';
import { weatherCredit } from '../utils/weatherMap';

function getWeatherIcon(code: number): string {
  if (code === 0)  return '/weather/clear.svg';
  if (code <= 2)   return '/weather/partly-cloudy.svg';
  if (code === 3)  return '/weather/overcast.svg';
  if (code <= 48)  return '/weather/fog.svg';
  if (code <= 57)  return '/weather/drizzle.svg';
  if (code <= 67)  return '/weather/rain.svg';
  if (code <= 77)  return '/weather/snow.svg';
  if (code <= 82)  return '/weather/showers.svg';
  if (code <= 86)  return '/weather/snow.svg';
  return '/weather/storm.svg';
}

function getWeatherEmoji(code: number): string {
  if (code === 0)  return '☀️';
  if (code <= 2)   return '🌤️';
  if (code === 3)  return '☁️';
  if (code <= 48)  return '🌫️';
  if (code <= 57)  return '🌦️';
  if (code <= 67)  return '🌧️';
  if (code <= 77)  return '🌨️';
  if (code <= 82)  return '🌦️';
  if (code <= 86)  return '🌨️';
  return '⛈️';
}

/**
 * ca_preview only: the store chrome's line-icon set (24px grid, 1.8 stroke,
 * round caps) for the forecast, in place of the emoji glyphs. Rendered hidden
 * and shown by html.ca-preview CSS, so production and EscapeTo are unchanged.
 */
function WxLineIcon({ code }: { code: number }) {
  const sun = <><circle cx="12" cy="12" r="4" /><path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" /></>;
  const cloud = 'M7 18.5h10a4 4 0 0 0 .6-7.95A5.5 5.5 0 0 0 7 11.5a3.5 3.5 0 0 0 0 7z';
  let body: React.ReactNode;
  if (code === 0) body = sun;
  else if (code <= 2) body = <><path d="M8.5 4.5v1.2M3.5 9.5h1.2M4.9 5.9l.85.85M12.1 5.9l-.85.85" /><path d="M5.6 11.6a3.2 3.2 0 0 1 5.5-3.1" /><path d="M9 19.5h8.5a3.5 3.5 0 0 0 .5-6.96A4.8 4.8 0 0 0 9 13a3.25 3.25 0 0 0 0 6.5z" /></>;
  else if (code === 3) body = <path d={cloud} />;
  else if (code <= 48) body = <><path d="M7 13.5h10a4 4 0 0 0 .6-7.95A5.5 5.5 0 0 0 7 6.5a3.5 3.5 0 0 0 0 7z" /><path d="M4 17h16M6 20.5h12" /></>;
  else if (code <= 67 || (code >= 80 && code <= 82)) body = <><path d="M7 14.5h10a4 4 0 0 0 .6-7.95A5.5 5.5 0 0 0 7 7.5a3.5 3.5 0 0 0 0 7z" /><path d="M8.5 17.5 7.5 20M12.5 17.5l-1 2.5M16.5 17.5l-1 2.5" /></>;
  else if (code <= 86) body = <><path d="M7 14.5h10a4 4 0 0 0 .6-7.95A5.5 5.5 0 0 0 7 7.5a3.5 3.5 0 0 0 0 7z" /><path d="M8 18.5h.01M12 20h.01M16 18.5h.01" strokeWidth="2.6" /></>;
  else body = <><path d="M7 14.5h10a4 4 0 0 0 .6-7.95A5.5 5.5 0 0 0 7 7.5a3.5 3.5 0 0 0 0 7z" /><path d="m12.5 15.5-2 3.5h3l-2 3.5" /></>;
  return (
    <svg className="ca-wx-line" style={{ display: 'none' }} width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{body}</svg>
  );
}

function getWeatherLabel(code: number): string {
  if (code === 0)  return 'Clear sky';
  if (code <= 2)   return 'Partly cloudy';
  if (code === 3)  return 'Overcast';
  if (code <= 48)  return 'Fog';
  if (code <= 57)  return 'Drizzle';
  if (code <= 67)  return 'Rain';
  if (code <= 77)  return 'Snow';
  if (code <= 82)  return 'Showers';
  if (code <= 86)  return 'Snow showers';
  return 'Thunderstorm';
}

function formatDayName(dateStr: string): { label: string; date: string; isToday: boolean; fullDay: string } {
  const date = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const diff = Math.round((date.getTime() - today.getTime()) / 86_400_000);
  const fullDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const shortDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  return {
    label: diff === 0 ? 'TODAY' : shortDays[date.getDay()],
    date: `${date.getDate()}/${date.getMonth() + 1}`,
    isToday: diff === 0,
    fullDay: diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : fullDays[date.getDay()],
  };
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch {
    return '';
  }
}

function uvLabel(uv: number): string {
  if (uv <= 2) return 'Low';
  if (uv <= 5) return 'Moderate';
  if (uv <= 7) return 'High';
  if (uv <= 10) return 'Very High';
  return 'Extreme';
}

function DayTooltip({ day }: { day: WeatherDay }) {
  const { fullDay } = formatDayName(day.date);
  const label = getWeatherLabel(day.weatherCode);
  const uv = Math.round(day.uvIndex);

  return (
    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 p-4 text-left pointer-events-none">
      <div className="font-semibold text-gray-800 text-sm mb-2">{fullDay} — {label}</div>
      <div className="grid grid-cols-2 gap-y-1.5 text-xs text-gray-600">
        <div>🌡️ High / Low</div>
        <div className="font-medium text-gray-800">{Math.round(day.tempMax)}° / {Math.round(day.tempMin)}°</div>

        <div>💧 Rain chance</div>
        <div className="font-medium text-gray-800">{day.precipitationProbability}%</div>

        {day.precipitation > 0 && <>
          <div>🌧️ Rainfall</div>
          <div className="font-medium text-gray-800">{day.precipitation.toFixed(1)} mm</div>
        </>}

        <div>💨 Wind</div>
        <div className="font-medium text-gray-800">{Math.round(day.windspeed)} km/h</div>

        <div>☀️ UV Index</div>
        <div className="font-medium text-gray-800">{uv} — {uvLabel(uv)}</div>

        {day.sunrise && <>
          <div>🌅 Sunrise</div>
          <div className="font-medium text-gray-800">{formatTime(day.sunrise)}</div>
        </>}

        {day.sunset && <>
          <div>🌇 Sunset</div>
          <div className="font-medium text-gray-800">{formatTime(day.sunset)}</div>
        </>}
      </div>
      {/* Arrow */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white" />
    </div>
  );
}

interface Props {
  forecast: WeatherForecastType;
  campsiteName: string;
}

export default function WeatherForecast({ forecast, campsiteName }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [hoveredDay, setHoveredDay] = useState<string | null>(null);
  const { days } = forecast;
  const credit = weatherCredit(forecast.source);
  const previewDays = days.slice(0, 5);
  const visibleDays = expanded ? days : previewDays;

  return (
    <section id="weather-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-slate-800">Weather Forecast</h2>
        {/* Credit whoever answered: the MET Norway fallback (weather_service)
            is CC BY 4.0 and must not be labelled as Open-Meteo. */}
        <a href={credit.href} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-400 hover:underline">
          {credit.label}
        </a>
      </div>

      <div className={`flex gap-2 overflow-x-auto pb-2 sm:grid sm:overflow-visible ${expanded ? 'sm:grid-cols-5 md:grid-cols-7' : 'sm:grid-cols-5'}`}>
        {visibleDays.map((day) => {
          const { label, isToday } = formatDayName(day.date);
          const emoji = getWeatherEmoji(day.weatherCode);
          const highRain = day.precipitationProbability > 40;

          return (
            <div
              key={day.date}
              className="relative flex flex-col items-center rounded-xl py-4 px-2 border cursor-default transition-all hover:shadow-md min-w-[100px] sm:min-w-0 flex-shrink-0 sm:flex-shrink"
              style={isToday
                ? { background: 'rgba(0, 102, 204, 0.10)', borderColor: 'rgba(0, 102, 204, 0.30)' }
                : { background: 'rgba(0, 82, 163, 0.03)', borderColor: 'rgba(0, 82, 163, 0.10)' }
              }
              onMouseEnter={() => setHoveredDay(day.date)}
              onMouseLeave={() => setHoveredDay(null)}
            >
              {/* Tooltip */}
              {hoveredDay === day.date && <DayTooltip day={day} />}

              {/* Day label */}
              <span className={`text-xs font-bold tracking-wide ${isToday ? 'text-[#1558b0]' : 'text-slate-500'}`}>
                {label}
              </span>

              {/* Weather icon */}
              <span className="ca-wx-emoji text-3xl leading-none my-3">{emoji}</span>
              <WxLineIcon code={day.weatherCode} />

              {/* Temperature */}
              <div className="text-base font-bold text-slate-800">
                {Math.round(day.tempMax)}° <span className="text-slate-400 font-normal">/</span> {Math.round(day.tempMin)}°
              </div>

              {/* Rain probability */}
              <div className="mt-1.5 flex items-center gap-0.5">
                <span className={`text-xs ${highRain ? 'font-semibold text-[#1558b0]' : 'text-slate-400'}`}>
                  {day.precipitationProbability > 0 ? (
                    <>💧 {day.precipitationProbability}%</>
                  ) : '—'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {days.length > 5 && (
        <button
          onClick={() => { if (!expanded) trackWeatherExpand(campsiteName); setExpanded(!expanded); }}
          className="mt-4 w-full text-center text-sm text-[#1558b0] hover:text-[#1558b0] font-medium py-2 rounded-lg hover:bg-[#e3f2fd] transition-colors"
        >
          {expanded ? 'Show 5-day forecast' : 'Show 14-day forecast'}
        </button>
      )}
    </section>
  );
}
